import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { storefrontAPI } from "@/services/api";
import { useToast } from "@/hooks/use-toast";

/** How often the open-order badge checks for new website orders. */
const POLL_MS = 15_000;

/**
 * Website-order notifications for the signed-in app.
 *
 * Polls the company-scoped open-order count (a bare number — no customer PII
 * crosses the wire per tick) and, when it *rises*, plays the notification
 * chime and raises a toast so staff know a buyer has ordered. The returned
 * number is what the header badge shows, so it grows as orders come in and
 * drops as they are fulfilled.
 *
 * Security / politeness notes:
 * - The endpoint sits behind the normal bearer-token auth and is filtered by
 *   the caller's company on the server, so one tenant can never see another's
 *   count.
 * - Polling stops while the tab is hidden (`refetchIntervalInBackground`), and
 *   a failed poll is not retried in a loop (`retry: false`).
 * - The chime only ever plays after the first successful observation, so an
 *   existing backlog of orders does not beep the moment the app opens, and
 *   browsers block it until the user has interacted with the app anyway.
 */
export function useOrderNotifications() {
  const { toast } = useToast();
  const lastSeenRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const query = useQuery({
    queryKey: ["openWebsiteOrders"],
    queryFn: storefrontAPI.getOpenOrderCount,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    retry: false,
    staleTime: POLL_MS,
  });

  const open = query.data?.open ?? 0;

  useEffect(() => {
    const count = query.data?.open;
    if (count === undefined) return;

    const previous = lastSeenRef.current;
    lastSeenRef.current = count;

    // First observation only records the baseline.
    if (previous === null) return;

    if (count > previous) {
      const added = count - previous;
      if (!audioRef.current) {
        audioRef.current = new Audio(`${import.meta.env.BASE_URL}notification.wav`);
      }
      audioRef.current.currentTime = 0;
      // Autoplay can be rejected until the user has interacted with the page;
      // the toast still carries the news, so swallow the rejection.
      void audioRef.current.play().catch(() => {});

      toast({
        title: added === 1 ? "New website order" : `${added} new website orders`,
        description:
          count === 1
            ? "1 order is waiting to be fulfilled"
            : `${count} orders are waiting to be fulfilled`,
      });
    }
  }, [query.data, toast]);

  return { open, isLoading: query.isLoading };
}
